import { Icon } from "@iconify/react/dist/iconify.js"

const LIMIT_OPTIONS = [10, 20, 50, 100, 200];

const PAGE_BUTTON = "tw-flex tw-items-center tw-justify-center tw-min-w-[32px] tw-h-8 tw-px-2 tw-rounded-md tw-border tw-border-solid tw-text-xs tw-font-medium tw-transition-colors disabled:tw-opacity-40 disabled:tw-cursor-not-allowed";
const PAGE_IDLE = "bg-base border-neutral-200 text-secondary-light enabled:hover:tw-bg-[var(--neutral-100)]";
const PAGE_ACTIVE = "bg-primary-600 border-primary-600 tw-text-white";

// First, last and the pages either side of the current one: 1 … 4 5 6 … 12.
// A gap of a single page shows that page, since "…" would take the same space.
export const getPageItems = (currentPage, totalPages) => {
    const pages = [...new Set([1, currentPage - 1, currentPage, currentPage + 1, totalPages])]
        .filter((p) => p >= 1 && p <= totalPages)
        .sort((a, b) => a - b);

    return pages.flatMap((page, index) => {
        const gap = index === 0 ? 1 : page - pages[index - 1];
        if (gap === 2) return [page - 1, page];
        if (gap > 2) return [`gap-${page}`, page];
        return [page];
    });
};

export const PaginationLimit = ({ currentPage = 1, totalRecords = 10, limit = 10, handleLimitChange = () => void (0), handlePageChange = () => { } }) => {
    // A limit set elsewhere (a page defaulting to 25, say) stays selectable.
    const limitOptions = LIMIT_OPTIONS.includes(Number(limit))
        ? LIMIT_OPTIONS
        : [...LIMIT_OPTIONS, Number(limit)].sort((a, b) => a - b);

    const page = Number(currentPage) || 1;
    const totalPages = Math.max(1, Math.ceil(totalRecords / limit));

    // "Showing 31 to 40 of 35" was possible on the last page.
    const firstRecord = totalRecords === 0 ? 0 : (page - 1) * limit + 1;
    const lastRecord = Math.min(page * limit, totalRecords);

    return (
        <div className="mt-24 tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center sm:tw-justify-between tw-gap-3">
            <span className="text-secondary-light tw-text-xs">
                Showing <span className="text-primary-light tw-font-semibold">{firstRecord}–{lastRecord}</span> of{' '}
                <span className="text-primary-light tw-font-semibold">{totalRecords}</span> records
            </span>

            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between sm:tw-justify-end tw-gap-x-4 tw-gap-y-2">
                <label className="tw-flex tw-items-center tw-gap-2 tw-mb-0 text-secondary-light tw-text-xs">
                    Rows per page
                    {/* Fixed choices. These were derived from the record count and the
                        current page, so a short list only ever offered 10 and 20 and the
                        options changed as you paged. `value` + onChange (not defaultValue
                        with `selected` options) keeps the box showing the live limit. */}
                    <select
                        className="form-select form-select-sm w-auto tw-h-8 tw-min-h-0 tw-py-0 tw-leading-[30px] tw-text-xs tw-rounded-md"
                        value={limit}
                        onChange={handleLimitChange}
                    >
                        {limitOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </label>

                <nav aria-label="Pagination" className="tw-flex tw-flex-wrap tw-items-center tw-gap-1">
                    <button
                        type="button"
                        aria-label="Previous page"
                        className={`${PAGE_BUTTON} ${PAGE_IDLE}`}
                        disabled={page <= 1}
                        onClick={() => handlePageChange(page - 1)}
                    >
                        <Icon icon="mdi:chevron-left" className="tw-text-base" />
                    </button>

                    {getPageItems(page, totalPages).map((item) => (
                        typeof item === 'string' ? (
                            <span key={item} aria-hidden="true" className="tw-min-w-[24px] tw-text-center text-secondary-light tw-text-xs">…</span>
                        ) : (
                            <button
                                key={item}
                                type="button"
                                aria-label={`Page ${item}`}
                                aria-current={item === page ? 'page' : undefined}
                                className={`${PAGE_BUTTON} ${item === page ? PAGE_ACTIVE : PAGE_IDLE}`}
                                onClick={() => handlePageChange(item)}
                            >
                                {item}
                            </button>
                        )
                    ))}

                    <button
                        type="button"
                        aria-label="Next page"
                        className={`${PAGE_BUTTON} ${PAGE_IDLE}`}
                        disabled={page >= totalPages}
                        onClick={() => handlePageChange(page + 1)}
                    >
                        <Icon icon="mdi:chevron-right" className="tw-text-base" />
                    </button>
                </nav>
            </div>
        </div>
    )
}
